import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { getBlogList } from '../services/Api';
import { ArrowRight, User } from 'lucide-react';
import BlogPagination from '../components/BlogPagination';
import Footer from '../components/Footer';
import { GlobalContext } from '../GlobalContext';

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const {globalVar, setGlobalVar} = useContext(GlobalContext);

  // pagination state
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const [totalPages, setTotalPages] = useState(1);

 const getImageUrl = (path) => `${globalVar}/${path}`;

  const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};


  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const res = await getBlogList(page, pageSize);
      if (res.success) {
        setPosts(res.data.results);
        setTotalPages(Math.ceil(res.data.count / pageSize));
      }
      setIsLoading(false);
    };
    fetchPosts();
  }, [page]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Header */}
        <div className="text-center mb-16 sm:mb-24 pt-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-800 via-slate-700 to-slate-900">
            Insights & Stories
          </h1>
          <div className="h-1 w-20 mx-auto mt-6 rounded-full bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500" />
          <p className="mt-6 text-xl text-slate-600 max-w-3xl mx-auto">
            Explore our latest articles, thoughts, and expert perspectives
          </p>
        </div>

        {isLoading ? (
          // Loading state
          <div className="flex justify-center items-center h-64">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-32 bg-slate-200 rounded" />
              <div className="h-6 w-48 bg-slate-200 rounded" />
            </div>
          </div>
        ) : (
          // Loaded state: grid + pagination
          <>
            <div className="grid gap-8 sm:gap-12 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                >
                  {/* IMAGE + CATEGORY BADGE */}
                  {post.image && (
                    <div className="relative w-full aspect-[16/10] overflow-hidden">
                      <img
                        src={getImageUrl(
                          post.image.replace(/_[^/]+(?=\.\w+$)/, '')
                        )}
                        alt="off-plan properties Dubai"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 bg-black/70 text-white text-xs font-medium uppercase px-2 py-1 rounded-full">
                        {post.category || 'Blog'}
                      </span>
                    </div>
                  )}

                  {/* BODY */}
                  <div className="flex flex-col flex-grow p-5 sm:p-6">
                    {/* AUTHOR */}
                    <div className="flex items-center mb-4 space-x-3">
                      {post.author_image ? (
                        <img
                          src={post.author_image}
                          alt={post.author_name}
                          className="h-8 w-8 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                      ) : (
                        <User className="h-6 w-6 text-slate-400" />
                      )}
                      <span className="text-sm font-medium text-slate-700">
                        {post.author_name}
                      </span>
                    </div>

                    {/* TITLE */}
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-900 line-clamp-2 mb-2">
                      <Link
                        to={`/blog/${post.slug}`}
                        className="hover:underline decoration-2 underline-offset-2"
                      >
                        {post.title}
                      </Link>
                    </h2>

                    {/* DESCRIPTION */}
                    <p className="text-sm text-slate-600 line-clamp-3 flex-grow mb-4">
  {stripHtml(post.description)}
</p>


                    {/* FOOTER */}
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <time dateTime={post.created_at}>
                        {new Date(post.created_at).toLocaleDateString(
                          undefined,
                          { month: 'short', day: 'numeric', year: 'numeric' }
                        )}
                      </time>
                      <span>· {post.read_time || '2 min read'}</span>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800"
                      >
                        Read More
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

<BlogPagination
        page={page}
        totalPages={totalPages}
        onChange={setPage}
      />

          </>
        )}
      </div>
    <Footer />
    </div>
  );
}
